import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import BrandedVoucherLayout from '@/components/voucher/BrandedVoucherLayout';
import FlightVoucherBody from '@/components/voucher/FlightVoucherBody';
import HotelVoucherBody from '@/components/voucher/HotelVoucherBody';
import { ActivityVoucherBody, TransferVoucherBody, ItineraryVoucherBody } from '@/components/voucher/VoucherBodies';
import DestinationTips from './vouchers/DestinationTips';
import VoucherTerms from './vouchers/VoucherTerms';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PublicVoucher = () => {
  const { voucherId } = useParams();
  const [searchParams] = useSearchParams();
  // Per-download toggle for hotel cancellation policy, forwarded from
  // VoucherPreview as ?showCancellation=false when the agent turned it off.
  // Defaults to true (cancellation prints) when the param is absent.
  const showCancellation = searchParams.get('showCancellation') !== 'false';
  // Playwright PDF render appends ?pdf=1. In the PDF the branded page footer
  // (pdf_service.py) already prints the contact + page numbers, so we suppress
  // the in-body "Need help" footer note to avoid a duplicate/second footer.
  // The web view (no pdf=1) keeps the in-body footer since it has no page footer.
  const isPdfRender = searchParams.get('pdf') === '1';
  // Per-passenger flight voucher selector — strict integer guard so NaN
  // never reaches FlightVoucherBody. Missing / non-numeric / negative → null
  // → all passengers render (capped at 9 inside the body component).
  const _rawPaxIdx = searchParams.get('passengerIndex');
  const passengerIndex = _rawPaxIdx !== null && /^\d+$/.test(_rawPaxIdx)
    ? parseInt(_rawPaxIdx, 10)
    : null;
  const [voucher, setVoucher] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  // Opt this public/Playwright route out of the global @media print
  // visibility:hidden rule in index.css. Without this, page.pdf() blanks
  // the body and only the Playwright header/footer template survives.
  useEffect(() => {
    document.body.classList.add('pdf-public-route');
    return () => document.body.classList.remove('pdf-public-route');
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [vRes, cRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/vouchers/public/${voucherId}`),
          axios.get(`${BACKEND_URL}/api/public/company`).catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        setVoucher(vRes.data);
        setCompany(cRes.data);
      } catch (err) {
        if (cancelled) return;
        // Only a real 404 means the voucher genuinely doesn't exist — any
        // other failure (network blip, 500, timeout) is transient and must
        // not be mislabeled as "not found", which hides the real problem
        // and gives the user no reason to retry.
        if (err.response?.status === 404) {
          setError('Voucher not found');
        } else {
          setError('Something went wrong loading this voucher. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [voucherId, retryNonce]);

  // Set the document title to the TRV reference so the Playwright PDF
  // header (`<span class="title">`) picks it up on every page automatically.
  // Falls back to the internal voucherId for legacy docs without a TRV ref.
  useEffect(() => {
    if (voucher) {
      const ref = voucher.trvRef || voucher.voucherId || 'Voucher';
      document.title = ref;
    }
  }, [voucher]);

  // Auto-print when opened via the in-app "Print" button (?print=1). This
  // route has no app chrome, so the browser print maps the branded A4 layout
  // cleanly to the page (see the body.pdf-public-route rules in index.css).
  // Wait for the voucher (and its images/QR) to settle before firing.
  const autoPrint = searchParams.get('print') === '1';
  useEffect(() => {
    if (autoPrint && voucher) {
      const t = setTimeout(() => window.print(), 700);
      return () => clearTimeout(t);
    }
  }, [autoPrint, voucher]);

  if (loading) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !voucher) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-center px-4" data-testid="public-voucher-error">
        <p className="text-slate-500">{error || 'Voucher not found'}</p>
        {error && error !== 'Voucher not found' && (
          <button
            type="button"
            onClick={() => setRetryNonce((n) => n + 1)}
            data-testid="public-voucher-retry-btn"
            className="text-sm font-medium text-teal-600 hover:text-teal-700 underline"
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );

  const qrValue = voucher.publicUrl || `${window.location.origin}/voucher/${voucher.voucherId}`;

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="public-voucher-page">
      <div className="max-w-5xl mx-auto">
        <div className="shadow-2xl mx-auto bg-white">
          <BrandedVoucherLayout voucher={voucher} company={company} qrValue={qrValue} forPdf={false} hideFooterNote={isPdfRender}>
            {voucher.type === 'flight' && (
              <div className="flight-public-view">
                {/* Public flight view — lighten brand-tinted blocks so the
                    customer-facing PDF/web view stays soft and neutral. All
                    overrides live HERE (PublicVoucher.js); FlightVoucherBody
                    is untouched. Agent preview (VoucherPreview.js) keeps the
                    full brand tint. */}
                <style>{`
                  .flight-public-view [data-testid="flight-single-pax-banner"] {
                    background: #F8FAFC !important;
                    border: 1px solid #E5E7EB !important;
                    color: #334155 !important;
                  }
                  .flight-public-view [data-testid="flight-passenger-cap-warning"] {
                    background: #F8FAFC !important;
                    border: 1px solid #E5E7EB !important;
                  }
                  .flight-public-view [data-testid="flight-passenger-cap-warning"] div,
                  .flight-public-view [data-testid="flight-passenger-cap-warning"] strong {
                    color: #334155 !important;
                  }
                  .flight-public-view [data-testid="flight-passenger-cap-warning"] svg {
                    color: #94a3b8 !important;
                  }
                  .flight-public-view [data-testid="flight-trip-type-pill"] {
                    background: #F8FAFC !important;
                    border: 1px solid #E5E7EB !important;
                    color: #334155 !important;
                  }
                  /* Route hero — direct child div of either layout wrapper
                     carrying inline border-radius:12px. Excludes children
                     that already have a data-testid (segments-list, etc). */
                  .flight-public-view [data-testid="flight-rich-layout"] > div[style*="border-radius: 12px"]:not([data-testid]),
                  .flight-public-view [data-testid="flight-legacy-layout"] > div[style*="border-radius: 12px"]:not([data-testid]) {
                    background: #F8FAFC !important;
                    border: 1px solid #E5E7EB !important;
                  }
                  /* Section cards (SectionCard primitive uses .card + bg-slate-50) */
                  .flight-public-view .card {
                    background: #FFFFFF !important;
                    border-color: #E5E7EB !important;
                  }
                `}</style>
                <FlightVoucherBody
                  voucher={voucher}
                  company={company}
                  passengerIndex={passengerIndex}
                  isAgentView={false}
                />
              </div>
            )}
            {voucher.type === 'hotel' && <HotelVoucherBody voucher={voucher} company={company} showCancellation={showCancellation} />}
            {voucher.type === 'activity' && <ActivityVoucherBody voucher={voucher} company={company} />}
            {voucher.type === 'transfer' && <TransferVoucherBody voucher={voucher} company={company} />}
            {voucher.type === 'itinerary' && <ItineraryVoucherBody voucher={voucher} company={company} />}

            {voucher.destinationInfo?.generated && <DestinationTips tips={voucher.destinationInfo} />}
            {/* Terms & Cancellation Policy — for hotel vouchers this section is
                the cancellation policy, so honour the "Include cancellation
                policy" toggle (showCancellation) and hide it when turned off. */}
            {voucher.termsAndConditions && (voucher.type !== 'hotel' || showCancellation) && (
              <VoucherTerms terms={voucher.termsAndConditions} type={voucher.type} />
            )}
          </BrandedVoucherLayout>
        </div>
      </div>
    </div>
  );
};

export default PublicVoucher;
